
import React, { useEffect, useState } from 'react';
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Power, AlertCircle, CheckCircle2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import LoggingService from '@/services/LoggingService';
import useConnectionStatus from '@/hooks/useConnectionStatus';
import sharePointService from '@/services/sharePointService';

interface ConnectionStatusProps {
  siteUrl: string;
  autoTest?: boolean;
  onStatusChange?: (status: string) => void;
}

/**
 * Component for displaying SharePoint connection status
 */
const ConnectionStatus: React.FC<ConnectionStatusProps> = ({ 
  siteUrl, 
  autoTest = false, 
  onStatusChange 
}) => {
  const { toast } = useToast();
  const { connectionStatus, handleTestConnection: hookTestConnection } = useConnectionStatus();
  const [status, setStatus] = useState<'notConnected' | 'connecting' | 'connected' | 'error' | 'notConfigured'>('notConnected');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // If autoTest is enabled, test the connection when component mounts
    if (autoTest && siteUrl) {
      testConnectionStatus();
    }
  }, [siteUrl, autoTest]);

  useEffect(() => {
    if (onStatusChange) {
      onStatusChange(status);
    }
  }, [status, onStatusChange]);

  const testConnectionStatus = async () => {
    if (!siteUrl) {
      setStatus('notConfigured');
      return;
    }

    setIsLoading(true);
    setStatus('connecting');
    
    try {
      LoggingService.info("Testing SharePoint connection", { siteUrl }, "ConnectionStatus");
      
      // Initialize SharePoint with the provided site URL
      await sharePointService.initialize(siteUrl);
      
      // Test the connection
      const success = await sharePointService.testConnection();
      
      if (success) {
        setStatus('connected');
        LoggingService.info("SharePoint connection successful", { siteUrl }, "ConnectionStatus");
        toast({
          title: "Connection Successful",
          description: "Connected to SharePoint successfully.",
          variant: "default",
        });
      } else {
        setStatus('error');
        LoggingService.warn("SharePoint connection failed", { siteUrl }, "ConnectionStatus");
        toast({
          title: "Connection Failed",
          description: "Could not connect to SharePoint.",
          variant: "destructive",
        });
      }
    } catch (error) {
      setStatus('error');
      LoggingService.error("SharePoint connection error", error, "ConnectionStatus");
      toast({
        title: "Connection Error",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // If automatic testing is enabled and no site URL is provided,
  // show a "Not Configured" status
  if (autoTest && !siteUrl) {
    return (
      <Badge variant="outline" className="text-gray-500 border-gray-300">
        <AlertCircle className="h-3 w-3 mr-1" />
        Not Configured
      </Badge>
    );
  }

  // Map status to badge properties
  const getBadgeContent = () => {
    switch (status) {
      case 'notConnected':
        return {
          variant: "outline" as const,
          className: "text-gray-500 border-gray-300",
          icon: <Power className="h-3 w-3 mr-1" />,
          text: "Not Connected"
        };
      case 'connecting':
        return {
          variant: "outline" as const,
          className: "text-blue-500 border-blue-300",
          icon: <RefreshCw className="h-3 w-3 mr-1 animate-spin" />,
          text: "Connecting..."
        };
      case 'connected':
        return {
          variant: "outline" as const,
          className: "text-green-500 border-green-300",
          icon: <CheckCircle2 className="h-3 w-3 mr-1" />,
          text: "Connected"
        };
      case 'error':
        return {
          variant: "outline" as const,
          className: "text-red-500 border-red-300",
          icon: <AlertCircle className="h-3 w-3 mr-1" />,
          text: "Connection Error"
        };
      default:
        return {
          variant: "outline" as const,
          className: "text-gray-500 border-gray-300",
          icon: <Power className="h-3 w-3 mr-1" />,
          text: "Unknown Status"
        };
    }
  };

  const { variant, className, icon, text } = getBadgeContent();

  return (
    <Badge 
      variant={variant} 
      className={className}
      onClick={!isLoading ? testConnectionStatus : undefined}
      style={{ cursor: !isLoading ? 'pointer' : 'default' }}
    >
      {icon}
      {text}
    </Badge>
  );
};

export default ConnectionStatus;
